<template>
  <div class="tabs-component">
    <div class="is-hidden-tablet" style="margin: 10px -20px 30px -20px">
      <ul role="tablist" class="tabs-component-tabs">
        <li
          :style="{width: 100 / (tabs.length) + `%`, margin: 0}"
          v-for="(tab, i) in tabs"
          :key="i"
          :class="{ 'is-active': tab.isActive, 'is-disabled': isDisabled || tab.isDisabled }"
          class="tabs-component-tab"
          role="presentation"
          v-show="tab.isVisible"
        >
          <a v-if="!isDisabled" v-html="tab.header"
             :aria-controls="tab.hash"
             :aria-selected="tab.isActive"
             @click="selectTab(tab.hash, $event)"
             :href="tab.hash"
             class="tabs-component-tab-a"
             role="tab"
          ></a>
        </li>
      </ul>
      <hr class="selected-tab-indicator"
          :style="{width: 100 / (tabs.length) + `%`, 'background-color': '#3d83ee', 'transition-duration': '0.2s' ,transform: `translateX(${(100 / (tabs.length - 2) * activeTabIndex) + `%`} )`}">
      <hr>
      <br>
    </div>
    <ul role="tablist" class="tabs-component-tabs is-hidden-mobile">
      <li
        v-for="(tab, i) in tabs"
        :key="i"
        :class="{ 'is-active': tab.isActive, 'is-disabled': isDisabled || tab.isDisabled }"
        class="tabs-component-tab"
        role="presentation"
        v-show="tab.isVisible"
      >
        <a v-if="!isDisabled" v-html="tab.header"
           :aria-controls="tab.hash"
           :aria-selected="tab.isActive"
           @click="selectTab(tab.hash, $event)"
           :href="tab.hash"
           class="tabs-component-tab-a"
           role="tab"
        ></a>
      </li>
    </ul>
    <slot name="between"/>
    <div class="tabs-component-panels vld-parent">
      <slot/>
    </div>
  </div>
</template>

<script>
  import expiringStorage from '../expiringStorage';

  export default {
    props: {
      isDisabled: {
        type: Boolean,
        default: false
      },
      cacheLifetime: {
        default: 5,
      },
      beforeChange: {
        type: Function,
        default: () => null
      },
      options: {
        type: Object,
        required: false,
        default: () => ({
          useUrlFragment: true,
          defaultTabHash: null,
        }),
      },
    },

    data: () => ({
      tabs: [],
      activeTabHash: '',
      activeTabIndex: 0,
      lastActiveTabHash: '',
    }),

    computed: {
      storageKey() {
        return `vue-tabs-component.cache.${window.location.host}${window.location.pathname}`;
      },
    },

    mounted() {
      this.tabs = this.$children.filter(c => c.$el.classList.contains('tabs-component-panel'));

      window.addEventListener('hashchange', () => this.selectTab(window.location.hash));

      if (this.findTab(window.location.hash)) {
        this.selectTab(window.location.hash);
        return;
      }

      const previousSelectedTabHash = expiringStorage.get(this.storageKey);

      if (this.findTab(previousSelectedTabHash)) {
        this.selectTab(previousSelectedTabHash);
        return;
      }

      if (this.options.defaultTabHash !== null && this.findTab("#" + this.options.defaultTabHash)) {
        this.selectTab("#" + this.options.defaultTabHash);
        return;
      }

      if (this.tabs.length) {
        this.selectTab(this.tabs[0].hash);
      }
    },

    methods: {
      moveToTab(tab) {
        this.$emit('changed', {tab});

        this.activeTabHash = tab.hash;
        this.activeTabIndex = this.getTabIndex(tab.hash);

        this.lastActiveTabHash = this.activeTabHash = tab.hash;

        expiringStorage.set(this.storageKey, tab.hash, this.cacheLifetime);
      },

      findTab(hash) {
        return this.tabs.find(tab => tab.hash === hash);
      },

      async selectTab(selectedTabHash, event) {
        // See if we should store the hash in the url fragment.
        let tab = this.findTab(selectedTabHash);

        if (!tab) return

        if (event && !this.options.useUrlFragment) {
          event.preventDefault();
        }

        await this.beforeChange({name: tab.name, hash: tab.hash})

        if (this.isLoading) {
          return
        }

        const selectedTab = tab;

        if (!selectedTab) {
          return;
        }

        if (selectedTab.isDisabled) {
          event.preventDefault();
          return;
        }

        if (this.lastActiveTabHash === selectedTab.hash) {
          this.$emit('clicked', {tab: selectedTab});
          return;
        }

        this.tabs.forEach(tab => {
          tab.isActive = (tab.hash === selectedTab.hash);
        });

        this.moveToTab(selectedTab)
      },

      setTabVisible(hash, visible) {
        const tab = this.findTab(hash);

        if (!tab) {
          return;
        }

        tab.isVisible = visible;

        if (tab.isActive) {
          // If tab is active, set a different one as active.
          tab.isActive = visible;

          this.tabs.every((tab, index, array) => {
            if (tab.isVisible) {
              tab.isActive = true;

              return false;
            }

            return true;
          });
        }
      },

      getTabIndex(hash) {
        const tab = this.findTab(hash);

        return this.tabs.indexOf(tab);
      },

      getTabHash(index) {
        const tab = this.tabs.find(tab => this.tabs.indexOf(tab) === index);

        if (!tab) {
          return;
        }

        return tab.hash;
      },

      getActiveTab() {
        return this.findTab(this.activeTabHash);
      },

      getActiveTabIndex() {
        return this.getTabIndex(this.activeTabHash);
      },
    },
  };
</script>
<style scoped lang="scss">
  @import "../../../../sass/fct/variables";
  @import "../../../../node_modules/bulma/sass/utilities/mixins";

  .fct-box {
    @include tablet() {
      padding: 40px;
    }
  }

  hr {
    margin: 0
  }

  ul {
    margin-bottom: 30px;

    @include mobile() {
      margin-bottom: 10px;
    }


    > li {
      display: inline-block;
      padding: 5px 23px;


      &:first-child {
        margin-left: 27px;
      }

      &.is-active {
        > a {
          color: $active-color !important;
        }
      }

      > a {
        transition: color 0.2s ease-in;
        font-weight: 600;
        //border: none !important;
        display: inline-block;
        color: $light-grey;
        line-height: 1;
        font-size: 14px;
      }
    }
  }
</style>
